import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Interior Painting",
  "Exterior Painting",
  "Commercial Work",
  "Residential Home",
  "Detail / Touch-Ups",
  "Not sure yet",
];

const TIMEFRAME_OPTIONS = [
  "ASAP (within 1 week)",
  "Within the month",
  "Within 1-3 months",
  "Just exploring",
];

export default function EstimateForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    address: "",
    timeframe: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in name, email, phone, and service type.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setSubmitted(true);
      toast.success("Estimate request received. We'll be in touch within 24 hours.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us at 817-405-8718.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="estimate"
        className="py-24 lg:py-32 border-t border-[#E5E5E0] bg-[#1C1C1A] text-[#F5F5F0]"
        data-testid="estimate-section-success"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <CheckCircle2 size={48} className="mx-auto text-[#CC5500]" />
          <h2 className="mt-6 font-serif-display text-5xl lg:text-6xl tracking-tightest leading-[0.95]">
            Thanks, we've got it.
          </h2>
          <p className="mt-6 font-sans-body text-base lg:text-lg text-[#E5E5E0] max-w-xl mx-auto leading-relaxed">
            A member of the JSE crew will reach out within 24 hours to confirm a
            walk-through. If it's urgent, call us anytime at{" "}
            <a href="tel:+18174058718" className="text-[#CC5500] underline">817-405-8718</a>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="estimate"
      className="py-24 lg:py-32 border-t border-[#E5E5E0] bg-[#1C1C1A] text-[#F5F5F0]"
      data-testid="estimate-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="font-sans-body text-[11px] uppercase tracking-[0.3em] text-[#CC5500]">
            (Free Estimate)
          </span>
          <h2 className="mt-4 font-serif-display text-4xl lg:text-6xl tracking-tightest leading-[0.95]">
            Tell us about
            <br />
            <em className="font-light">your project.</em>
          </h2>
          <p className="mt-8 font-sans-body text-base text-[#E5E5E0]/80 leading-relaxed max-w-md">
            We respond within 24 hours, in person or by phone — never an automated
            chase. No upsells, just honest scope and pricing.
          </p>

          <div className="mt-10 space-y-4 font-sans-body text-sm text-[#E5E5E0]/80">
            <div className="flex gap-3">
              <span className="text-[#CC5500]">→</span>
              <span>No-pressure walk-through</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[#CC5500]">→</span>
              <span>Itemized written estimate</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[#CC5500]">→</span>
              <span>Same crew start to finish</span>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="lg:col-span-7 space-y-6"
          data-testid="estimate-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Full Name *" value={form.name} onChange={set("name")} testid="form-name" />
            <Field label="Email *" type="email" value={form.email} onChange={set("email")} testid="form-email" />
            <Field label="Phone *" value={form.phone} onChange={set("phone")} testid="form-phone" />
            <Select label="Service Type *" value={form.service_type} onChange={set("service_type")} options={SERVICE_OPTIONS} testid="form-service" />
            <Field label="Address (optional)" value={form.address} onChange={set("address")} testid="form-address" />
            <Select label="Timeframe" value={form.timeframe} onChange={set("timeframe")} options={TIMEFRAME_OPTIONS} testid="form-timeframe" />
          </div>

          <div>
            <label className="block font-sans-body text-[11px] uppercase tracking-[0.25em] text-[#E5E5E0]/70 mb-2">
              Project Details
            </label>
            <textarea
              value={form.details}
              onChange={set("details")}
              rows={4}
              placeholder="Rooms, colors, special considerations…"
              className="w-full bg-transparent border-0 border-b border-[#E5E5E0]/30 focus:border-[#CC5500] focus:outline-none py-3 text-base font-sans-body text-[#F5F5F0] placeholder:text-[#E5E5E0]/30 resize-none transition-colors"
              data-testid="form-details"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="form-submit"
            className="group inline-flex items-center gap-3 bg-[#CC5500] text-[#F5F5F0] hover:bg-[#F5F5F0] hover:text-[#1C1C1A] transition-colors duration-300 px-8 py-4 text-sm font-sans-body uppercase tracking-[0.2em] disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Request Estimate"}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block font-sans-body text-[11px] uppercase tracking-[0.25em] text-[#E5E5E0]/70 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-0 border-b border-[#E5E5E0]/30 focus:border-[#CC5500] focus:outline-none py-3 text-base font-sans-body text-[#F5F5F0] transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block font-sans-body text-[11px] uppercase tracking-[0.25em] text-[#E5E5E0]/70 mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-0 border-b border-[#E5E5E0]/30 focus:border-[#CC5500] focus:outline-none py-3 text-base font-sans-body text-[#F5F5F0] transition-colors appearance-none cursor-pointer"
        data-testid={testid}
      >
        <option value="" className="bg-[#1C1C1A] text-[#F5F5F0]">
          Select…
        </option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#1C1C1A] text-[#F5F5F0]">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
